import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { useSession, useTenant } from '@zhiyue/core';
import { Alert, App, Button, Space, Tabs, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { queryReceipts } from '@/services/bff/receipt.api';
import { queryReceiptSchedules } from '@/services/bff/receipt-schedule.api';
import {
  enableReceiptPlanGroup,
  getReceiptContext,
  getReceiptContracts,
  getReceiptSummary,
  getReceiptSyncStatus,
  getRegistration,
  getWorkflowPlan,
  queryReceiptContractSummaries,
  queryReceiptRegistrations,
  queryReceiptTransactions,
  queryWorkflowPlans,
  receiptPlanAction,
  registrationAction,
  syncReceiptProjections,
} from '@/services/bff/receipt-workflow.api';
import type {
  ReceiptPlan,
  ReceiptRegistration,
  ReceiptSummary,
  ReceiptWorkflowQuery,
} from '@/services/bff/types/receipt-workflow';
import { extractList, extractTotal } from '@/utils/apiResponse';
import { MemberPicker } from '../member-picker';
import { ReceiptDetail } from './receipt-detail';
import { ReceiptForm } from './receipt-form';
import { displayReceiptMoney } from './receipt-money';
import { receiptFieldText, receiptStatusText } from './receipt-status';
import styles from './style.module.less';
import { useReceiptCapabilities } from './use-receipt-capabilities';
import { useReceiptCommand } from './use-receipt-command';

type Tab = 'registrations' | 'transactions' | 'summary' | 'plans';
type Row = Record<string, any> & { uuid: string };
export function ReceiptWorkspace(props: {
  initialTab?: Tab;
  contractUuid?: string;
  customerUuid?: string;
  embedded?: boolean;
}) {
  const { currentTenantUuid } = useTenant();
  const { currentUser } = useSession();
  return (
    <ReceiptWorkspaceContent
      key={`${currentTenantUuid}:${currentUser?.memberUuid}`}
      {...props}
    />
  );
}
function ReceiptWorkspaceContent({
  initialTab = 'registrations',
  contractUuid,
  customerUuid,
  embedded = false,
}: {
  initialTab?: Tab;
  contractUuid?: string;
  customerUuid?: string;
  embedded?: boolean;
}) {
  const { modal, message } = App.useApp();
  const { capabilities, loading, refresh } = useReceiptCapabilities();
  const actionRef = useRef<ActionType>(undefined);
  const [selectedContract, setSelectedContract] = useState<string>();
  const [childPlan, setChildPlan] = useState<string>();
  const [contextVersion, setContextVersion] = useState(0);
  const [syncState, setSyncState] = useState('');
  const [tab, setTab] = useState<Tab>(initialTab);
  const [editor, setEditor] = useState<{
    kind: 'plan' | 'registration';
    contractUuid?: string;
    planUuid?: string;
    record?: ReceiptPlan | ReceiptRegistration;
  }>();
  const [detailRevision, setDetailRevision] = useState(0);
  const [detail, setDetail] = useState<{
    kind: 'plans' | 'registrations' | 'transactions';
    uuid: string;
  }>();
  const [summary, setSummary] = useState<ReceiptSummary>();
  const [summaryError, setSummaryError] = useState(false);
  const summarySequence = useRef(0);
  const reload = useCallback(() => {
    setEditor(undefined);
    setChildPlan(undefined);
    setDetailRevision((value) => value + 1);
    actionRef.current?.reload();
  }, []);
  const command = useReceiptCommand(reload);
  const activeContract = contractUuid || selectedContract;
  useEffect(() => {
    if (!loading && !capabilities.enabled && initialTab !== 'plans')
      setTab('summary');
  }, [loading, capabilities.enabled, initialTab]);
  const actionAllowed = (action: string) =>
    capabilities.enabled &&
    capabilities.actions[action]?.allowed === true &&
    !command.blocked &&
    !command.busy;
  const edit = async (kind: 'plan' | 'registration', row: Row) => {
    try {
      const record =
        kind === 'plan'
          ? await getWorkflowPlan(row.uuid)
          : await getRegistration(row.uuid);
      setEditor({ kind, record, contractUuid: record.contractUuid });
    } catch {
      message.error('加载最新记录失败，请刷新后重试');
    }
  };
  const operate = (
    kind: 'plan' | 'registration',
    action: 'enable' | 'cancel' | 'delete' | 'submit' | 'withdraw',
    row: Row,
  ) => {
    const labels: Record<string, string> = {
      enable: '启用计划',
      cancel: '取消计划',
      delete: '删除草稿',
      submit: '提交财务',
      withdraw: '撤回登记',
    };
    modal.confirm({
      title: `确认${labels[action]}？`,
      content:
        action === 'enable'
          ? '启用将按合同中心确认条件处理，财务到账仍需单独确认。'
          : undefined,
      closable: { 'aria-label': '关闭' },
      keyboard: true,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await command.execute<ReceiptPlan | ReceiptRegistration>(
            labels[action],
            kind === 'plan' ? 'contract' : 'finance',
            (id) =>
              kind === 'plan'
                ? receiptPlanAction(
                    row.uuid,
                    action as 'enable' | 'cancel' | 'delete',
                    row.revision,
                    id,
                  )
                : registrationAction(
                    row.uuid,
                    action as 'submit' | 'withdraw' | 'delete',
                    row.revision,
                    id,
                  ),
          );
          message.success('操作成功');
          reload();
        } catch {
          /* Command feedback retains request ID and preserves recovery. */
        }
      },
    });
  };
  const enableGroup = async (row: Row) => {
    try {
      const context = await getReceiptContext(row.contractUuid);
      modal.confirm({
        title: '整组启用合同的草稿计划？',
        content: `合同：${row.contractName}。将启用 ${context.plans.filter((item) => item.status === 'DRAFT').length} 个草稿计划，并按 ${context.settlementAmountPolicy} 策略校验总额。`,
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        okText: '整组启用',
        cancelText: '取消',
        onOk: async () => {
          try {
            await command.execute('整组启用计划', 'contract', (id) =>
              enableReceiptPlanGroup(
                row.contractUuid,
                context.groupRevision,
                id,
              ),
            );
            reload();
          } catch {
            /* request recovery remains available */
          }
        },
      });
    } catch {
      message.error('计划组加载失败，请重试');
    }
  };
  const sync = async () => {
    try {
      setSyncState('正在同步，请稍候');
      const result = await syncReceiptProjections();
      setSyncState(
        result.initialized
          ? result.checkpoints.some((item) => item.last_error)
            ? '同步已有异常，请查看同步状态'
            : '同步完成'
          : '历史投影仍在建立，后台将继续补偿',
      );
      actionRef.current?.reload();
    } catch {
      setSyncState('同步暂未完成，请查看同步状态或稍后重试');
    }
  };
  const moneyColumn = (title: string, dataIndex: string): ProColumns<Row> => ({
    title,
    dataIndex,
    search: false,
    sorter: ['amount', 'plannedAmount'].includes(dataIndex),
    render: (_, row) => displayReceiptMoney(row[dataIndex]),
  });
  const statusColumn = (
    title: string,
    dataIndex: string,
    values: string[],
  ): ProColumns<Row> => ({
    title,
    dataIndex,
    valueType: 'select',
    search: values.length > 0,
    valueEnum: Object.fromEntries(
      values.map((value) => [
        value,
        { text: receiptFieldText(dataIndex, value) },
      ]),
    ),
    render: (_, row) => (
      <Tag>{receiptFieldText(dataIndex, row[dataIndex])}</Tag>
    ),
  });
  const columns: ProColumns<Row>[] = [
    { title: '关键词', dataIndex: 'keyword', hideInTable: true },
    {
      title: '数据范围',
      dataIndex: 'scope',
      hideInTable: true,
      valueType: 'select',
      initialValue: 'all',
      valueEnum: { self: '本人', team: '团队', all: '全部授权' },
    },
    {
      title: '合同',
      dataIndex: 'contractUuid',
      hideInTable: true,
      initialValue: activeContract,
      fieldProps: { disabled: Boolean(activeContract) },
      valueType: 'select',
      request: async () =>
        (
          await getReceiptContracts({
            scope: 'all',
            customerUuid,
            pageSize: 200,
          })
        ).items.map((item) => ({
          value: item.uuid,
          label: `${item.contractNo} · ${item.contractName}`,
        })),
    },
    {
      title: '负责人',
      dataIndex: 'ownerMemberUuid',
      hideInTable: true,
      formItemRender: () => <MemberPicker />,
    },
    {
      title: '合同编号',
      dataIndex: 'contractNo',
      search: false,
      fixed: 'left',
      width: 160,
    },
    {
      title: '合同名称',
      dataIndex: 'contractName',
      search: false,
      ellipsis: true,
      width: 200,
    },
    { title: '客户', dataIndex: 'customerName', search: false, width: 160 },
    { title: '币种', dataIndex: 'currency', search: false, width: 80 },
    ...(tab === 'plans'
      ? ([
          {
            title: '计划名称',
            dataIndex: 'planName',
            search: false,
            width: 180,
          },
          { title: '计划日期', dataIndex: 'plannedDate', search: false },
          { title: '到期日期', dataIndex: 'dueDate', search: false },
          moneyColumn('计划金额', 'plannedAmount'),
          moneyColumn('已确认金额', 'financeSettledAmount'),
          moneyColumn('待确认占用', 'occupyingAmount'),
          moneyColumn('可登记余额', 'availableAmount'),
          statusColumn('生命周期', 'status', [
            'DRAFT',
            'READY',
            'RECOGNIZED',
            'CANCELLED',
          ]),
          statusColumn('回款进度', 'progress', [
            'PENDING',
            'PARTIAL',
            'COMPLETED',
            'UNKNOWN',
          ]),
          {
            title: '逾期',
            dataIndex: 'overdue',
            valueType: 'select',
            valueEnum: { true: { text: '已逾期' }, false: { text: '未逾期' } },
            render: (_: unknown, row: Row) =>
              row.overdue == null ? (
                '待同步'
              ) : row.overdue ? (
                <Tag color="error">已逾期</Tag>
              ) : (
                '否'
              ),
          },
          statusColumn('财务同步', 'financeSyncStatus', []),
        ] as ProColumns<Row>[])
      : tab === 'summary'
        ? ([
            moneyColumn('合同金额', 'contractAmount'),
            moneyColumn('已确认回款', 'receivedAmount'),
            moneyColumn('剩余应收', 'remainingAmount'),
            moneyColumn('待确认占用', 'occupyingAmount'),
            moneyColumn('可登记余额', 'availableAmount'),
            moneyColumn('逾期金额', 'overdueAmount'),
            {
              title: '结算计划数量',
              dataIndex: 'settlementPlanCount',
              search: false,
            },
            {
              title: '最近财务同步',
              dataIndex: 'financeLastSyncedAt',
              search: false,
            },
          ] as ProColumns<Row>[])
        : ([
            {
              title: tab === 'registrations' ? '登记编号' : '财务单号',
              dataIndex:
                tab === 'registrations' ? 'registrationNo' : 'financeObjectNo',
              search: false,
            },
            moneyColumn('金额', 'amount'),
            {
              title: tab === 'registrations' ? '到账日期' : '确认时间',
              dataIndex:
                tab === 'registrations' ? 'receiptDate' : 'confirmedAt',
              search: false,
            },
            ...(tab === 'registrations'
              ? [
                  statusColumn('处理状态', 'status', [
                    'DRAFT',
                    'PENDING',
                    'CONFIRMED',
                    'REJECTED',
                    'WITHDRAWN',
                  ]),
                  { title: '登记人', dataIndex: 'creatorName', search: false },
                  {
                    title: '驳回原因',
                    dataIndex: 'rejectionReason',
                    search: false,
                  },
                ]
              : [
                  {
                    title: '交易类型',
                    dataIndex: 'type',
                    search: false,
                    render: (_: unknown, row: Row) =>
                      receiptStatusText[row.type] || row.type,
                  },
                ]),
          ] as ProColumns<Row>[])),
    ...(tab !== 'summary' && capabilities.enabled
      ? ([
          {
            title: '日期起',
            dataIndex: 'dateStart',
            valueType: 'date',
            hideInTable: true,
          },
          {
            title: '日期止',
            dataIndex: 'dateEnd',
            valueType: 'date',
            hideInTable: true,
          },
        ] as ProColumns<Row>[])
      : []),
    {
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      width: tab === 'plans' ? 300 : 240,
      render: (_, row) => (
        <Space wrap>
          {tab !== 'summary' && capabilities.enabled && (
            <Button
              type="link"
              onClick={() =>
                setDetail({
                  kind: tab as 'plans' | 'registrations' | 'transactions',
                  uuid: row.uuid,
                })
              }
            >
              详情
            </Button>
          )}
          {tab === 'summary' && (
            <>
              <Button
                type="link"
                disabled={!actionAllowed('create')}
                onClick={() =>
                  setEditor({
                    kind: 'registration',
                    contractUuid: row.contractUuid || row.uuid,
                  })
                }
              >
                发起回款
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setSelectedContract(row.contractUuid || row.uuid);
                  setTab('plans');
                  actionRef.current?.setPageInfo?.({ current: 1 });
                }}
              >
                查看计划
              </Button>
            </>
          )}
          {(tab === 'plans' || tab === 'registrations') &&
            capabilities.enabled && (
              <>
                {row.actions?.edit && (
                  <Tooltip title={row.actions.edit.reason}>
                    <Button
                      type="link"
                      disabled={
                        !row.actions.edit.allowed ||
                        command.blocked ||
                        command.busy
                      }
                      onClick={() =>
                        void edit(
                          tab === 'plans' ? 'plan' : 'registration',
                          row,
                        )
                      }
                    >
                      编辑
                    </Button>
                  </Tooltip>
                )}
                {(tab === 'plans'
                  ? ['enable', 'cancel', 'delete']
                  : ['submit', 'withdraw', 'delete']
                ).map(
                  (action) =>
                    row.actions?.[action] && (
                      <Tooltip key={action} title={row.actions[action].reason}>
                        <Button
                          type="link"
                          danger={action === 'delete' || action === 'cancel'}
                          disabled={
                            !row.actions[action].allowed ||
                            command.blocked ||
                            command.busy
                          }
                          onClick={() =>
                            operate(
                              tab === 'plans' ? 'plan' : 'registration',
                              action as
                                | 'enable'
                                | 'cancel'
                                | 'delete'
                                | 'submit'
                                | 'withdraw',
                              row,
                            )
                          }
                        >
                          {
                            {
                              enable: '启用',
                              cancel: '取消',
                              delete: '删除',
                              submit: '提交财务',
                              withdraw: '撤回',
                            }[action]
                          }
                        </Button>
                      </Tooltip>
                    ),
                )}
                {tab === 'plans' && row.status === 'DRAFT' && (
                  <Button
                    type="link"
                    disabled={!actionAllowed('plan-enable')}
                    onClick={() => void enableGroup(row)}
                  >
                    整组启用
                  </Button>
                )}
                {tab === 'plans' && (
                  <Button
                    type="link"
                    disabled={
                      !actionAllowed('create') ||
                      !['READY', 'RECOGNIZED'].includes(row.status)
                    }
                    onClick={() =>
                      setEditor({
                        kind: 'registration',
                        contractUuid: row.contractUuid,
                        planUuid: row.uuid,
                      })
                    }
                  >
                    发起回款
                  </Button>
                )}
              </>
            )}
        </Space>
      ),
    },
  ];
  const items =
    initialTab === 'plans' && !embedded
      ? [{ key: 'plans', label: '回款计划' }]
      : [
          ...(embedded ? [{ key: 'plans', label: '回款计划' }] : []),
          {
            key: 'registrations',
            label: '回款登记',
            disabled: !capabilities.enabled,
          },
          {
            key: 'transactions',
            label: '已确认回款',
            disabled: !capabilities.enabled,
          },
          { key: 'summary', label: '合同回款汇总' },
        ];
  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      {!capabilities.enabled && (
        <Alert
          showIcon
          type="info"
          title={loading ? '正在检查回款能力' : capabilities.reason}
          action={
            <Button loading={loading} onClick={refresh}>
              重试连接
            </Button>
          }
        />
      )}
      {command.feedback}
      {summaryError && (
        <Alert type="warning" title="回款统计暂不可用，未将未知金额记为零。" />
      )}
      {summary && (
        <>
          {!summary.complete && (
            <Alert
              type="warning"
              title="逐笔财务来源未完整接入，当前明细与汇总尚待核对。"
            />
          )}
          <div className={styles.summary}>
            {summary.currencies.map((row) => (
              <span key={row.currency}>
                <strong>{row.currency}</strong>　已收{' '}
                {displayReceiptMoney(row.receivedAmount)}　待确认{' '}
                {displayReceiptMoney(row.occupyingAmount)}　可登记{' '}
                {displayReceiptMoney(row.availableAmount)}　逾期{' '}
                {displayReceiptMoney(row.overdueAmount)}
              </span>
            ))}
          </div>
        </>
      )}
      {selectedContract && !contractUuid && (
        <Button onClick={() => setSelectedContract(undefined)}>
          清除合同筛选
        </Button>
      )}
      {syncState && <Alert type="info" title={syncState} />}
      <Tabs
        activeKey={tab}
        onChange={(value) => {
          setTab(value as Tab);
          setSummary(undefined);
        }}
        items={items}
      />
      <ProTable<Row>
        key={`${tab}:${capabilities.enabled}:${activeContract || 'all'}`}
        actionRef={actionRef}
        rowKey="uuid"
        size="small"
        columns={columns}
        scroll={{ x: 'max-content' }}
        search={{ labelWidth: 90 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        options={{ density: false }}
        toolBarRender={() => [
          <Tooltip
            key="new"
            title={
              capabilities.reason ||
              capabilities.actions[tab === 'plans' ? 'plan-create' : 'create']
                ?.reason
            }
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={
                !actionAllowed(tab === 'plans' ? 'plan-create' : 'create')
              }
              onClick={() =>
                setEditor({
                  kind: tab === 'plans' ? 'plan' : 'registration',
                  contractUuid: activeContract,
                })
              }
            >
              {tab === 'plans' ? '新增计划' : '发起回款'}
            </Button>
          </Tooltip>,
          <Button
            key="sync"
            disabled={!actionAllowed('sync')}
            onClick={() => void sync()}
          >
            同步与对账
          </Button>,
          <Button
            key="syncStatus"
            disabled={!actionAllowed('sync')}
            onClick={async () => {
              try {
                const result = await getReceiptSyncStatus();
                setSyncState(
                  result.checkpoints
                    .map(
                      (item) =>
                        `${item.center}: ${item.last_error || item.last_synced_at || '尚未同步'}`,
                    )
                    .join('；') || '尚未启动历史投影同步',
                );
              } catch {
                setSyncState('同步状态读取失败');
              }
            }}
          >
            同步状态
          </Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={() => actionRef.current?.reload()}
          >
            刷新
          </Button>,
        ]}
        request={async (params, sort) => {
          const [sortBy, order] = Object.entries(sort || {})[0] || [];
          const {
            current = 1,
            pageSize = 20,
            keyword,
            scope,
            ownerMemberUuid,
            status,
            progress,
            overdue,
            dateStart,
            dateEnd,
          } = params;
          const filters: ReceiptWorkflowQuery = {
            page: current,
            pageSize,
            keyword,
            scope: scope || 'all',
            ownerMemberUuid,
            customerUuid,
            contractUuid: activeContract || params.contractUuid,
            status,
            progress,
            overdue:
              overdue === undefined ? undefined : String(overdue) === 'true',
            dateStart,
            dateEnd,
          };
          if (!capabilities.enabled && !['plans', 'summary'].includes(tab))
            return { data: [], total: 0, success: true };
          const response =
            tab === 'plans'
              ? capabilities.enabled
                ? await queryWorkflowPlans(filters)
                : await queryReceiptSchedules(filters)
              : tab === 'registrations'
                ? await queryReceiptRegistrations(filters)
                : tab === 'transactions'
                  ? await queryReceiptTransactions(filters)
                  : capabilities.enabled
                    ? await queryReceiptContractSummaries(filters)
                    : await queryReceipts(filters);
          const sequence = ++summarySequence.current;
          if (capabilities.enabled)
            void getReceiptSummary(filters)
              .then((value) => {
                if (sequence === summarySequence.current) {
                  setSummary(value);
                  setSummaryError(false);
                }
              })
              .catch(() => {
                if (sequence === summarySequence.current) {
                  setSummary(undefined);
                  setSummaryError(true);
                }
              });
          return {
            data: extractList<Row>(response),
            total: extractTotal<Row>(response),
            success: true,
          };
        }}
      />
      {editor && (
        <ReceiptForm
          {...editor}
          customerUuid={customerUuid}
          command={command}
          canSubmit={actionAllowed('submit')}
          canEnable={actionAllowed('plan-enable')}
          contextVersion={contextVersion}
          onCreatePlan={(uuid) => setChildPlan(uuid)}
          onClose={() => setEditor(undefined)}
          onSaved={reload}
        />
      )}
      {childPlan && (
        <ReceiptForm
          kind="plan"
          contractUuid={childPlan}
          command={command}
          canSubmit={false}
          canEnable={actionAllowed('plan-enable')}
          onClose={() => setChildPlan(undefined)}
          onSaved={() => {
            setChildPlan(undefined);
            setContextVersion((value) => value + 1);
            actionRef.current?.reload();
          }}
        />
      )}
      {detail && (
        <ReceiptDetail
          key={`${detail.uuid}:${detailRevision}`}
          {...detail}
          onEdit={(record) =>
            setEditor({
              kind: detail.kind === 'plans' ? 'plan' : 'registration',
              record,
              contractUuid: record.contractUuid,
            })
          }
          onRegister={
            actionAllowed('create')
              ? (plan) =>
                  setEditor({
                    kind: 'registration',
                    contractUuid: plan.contractUuid,
                    planUuid: plan.uuid,
                  })
              : undefined
          }
          onClose={() => setDetail(undefined)}
        />
      )}
    </Space>
  );
}
